/**
 * Browser polyfills for Electron APIs
 * Provides compatible replacements for electron shell, clipboard, etc.
 */

import { filterFilesByExtensions, sortFilesNumerically, filterTopLevelFiles } from '@/utils/fileInput';

/**
 * Shell API replacement
 * Provides openExternal for opening URLs in new tabs
 */
export const shell = {
  /**
   * Open a URL in a new browser tab
   * @param {string} url - URL to open
   */
  openExternal(url) {
    // Use anchor element approach for more reliable new tab behavior
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
};

/**
 * Clipboard API replacement
 * Uses browser Clipboard API
 */
export const clipboard = {
  /**
   * Write text to clipboard
   * @param {string} text - Text to copy
   */
  async writeText(text) {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  },

  /**
   * Read text from clipboard
   * @returns {Promise<string>}
   */
  async readText() {
    try {
      return await navigator.clipboard.readText();
    } catch (err) {
      console.warn('Clipboard read not available');
      return '';
    }
  }
};

/**
 * Remote API replacement (no-op for most things)
 * Provides app info and dialog replacements
 */
export const remote = {
  app: {
    /**
     * Get app version
     * @returns {string}
     */
    getVersion() {
      return process.env.VUE_APP_VERSION || '0.0.0';
    },

    /**
     * Get path - returns appropriate browser storage location
     * @param {string} name - Path type (userData, temp, etc.)
     * @returns {string}
     */
    getPath(name) {
      // In browser, we use virtual paths
      return `browser://${name}`;
    },

    /**
     * Get name
     * @returns {string}
     */
    getName() {
      return 'EyeJack Creator Web';
    }
  },

  dialog: {
    /**
     * Show save dialog - returns a pseudo path for download
     * @param {Object} options 
     * @returns {Promise<{canceled: boolean, filePath: string}>}
     */
    async showSaveDialog(options) {
      // In browser, we'll just trigger a download
      return {
        canceled: false,
        filePath: options.defaultPath?.split('/').pop() || 'download',
        useDownload: true
      };
    },

    /**
     * Show open dialog - use file input
     * @param {Object} options 
     * @returns {Promise<{canceled: boolean, filePaths: string[]}>}
     */
    async showOpenDialog(options) {
      return new Promise((resolve) => {
        const input = document.createElement('input');
        input.type = 'file';
        
        // Track if we've already resolved to prevent double-resolution
        let resolved = false;
        const resolveOnce = (result) => {
          if (!resolved) {
            resolved = true;
            cleanup();
            resolve(result);
          }
        };
        
        // Support both singular and plural forms for multi-selection
        if (options.properties?.includes('multiSelections') || 
            options.properties?.includes('multiSelection')) {
          input.multiple = true;
        }
        
        // Support directory selection for image sequences
        if (options.properties?.includes('openDirectory')) {
          input.webkitdirectory = true;
          input.directory = true;
        }
        
        if (options.filters?.length > 0 && !input.webkitdirectory) {
          const extensions = options.filters
            .flatMap(f => f.extensions)
            .filter(e => e)
            .map(e => `.${e}`)
            .join(',');
          input.accept = extensions;
        }
        
        // Cancel detection. Modern browsers fire a `cancel` event on file inputs
        // (input.oncancel below). Older ones (Safari <16.4, Chrome <113) don't, so
        // we fall back to the window `focus` event. Either way we must NOT assume
        // "canceled" on a short fixed delay: a large selection (e.g. a 1000+ image
        // sequence) can take seconds to enumerate and populate input.files, with
        // `change` firing only afterwards. So we poll input.files and only declare
        // "canceled" after a grace period with no selection — long when the reliable
        // `cancel` event exists (then focus is just a safety net), shorter as a
        // legacy fallback.
        let dialogOpened = false;
        const hasCancelEvent = 'oncancel' in input;
        const focusCancelGraceMs = hasCancelEvent ? 30000 : 5000;
        
        const handleFocus = () => {
          if (!dialogOpened || resolved) return;
          const startedAt = Date.now();
          const poll = () => {
            if (resolved) return;
            // A selection populates input.files; onchange will resolve it. Keep
            // waiting (don't cancel) for however long that takes — large image
            // sequences can need several seconds to enumerate.
            if (input.files && input.files.length > 0) return;
            if (Date.now() - startedAt >= focusCancelGraceMs) {
              console.log('[FileDialog] Resolving as canceled (focus fallback, no selection)');
              resolveOnce({ canceled: true, filePaths: [] });
              return;
            }
            setTimeout(poll, 250);
          };
          poll();
        };
        
        const cleanup = () => {
          window.removeEventListener('focus', handleFocus);
          input.onchange = null;
          input.oncancel = null;
        };
        
        input.onchange = (event) => {
          console.log('[FileDialog] onchange fired, files:', event.target.files?.length);
          let files = Array.from(event.target.files || []);
          
          // If a directory was selected, keep only the files directly inside it
          // (one level — ignore nested subfolders), then filter + sort for image
          // sequences (shared with the drop path via @/utils/fileInput).
          if (input.webkitdirectory) {
            files = filterTopLevelFiles(files);
            if (options.filters?.length > 0) {
              const validExtensions = options.filters.flatMap(f => f.extensions);
              files = filterFilesByExtensions(files, validExtensions);
            }
            files = sortFilesNumerically(files);
          }
          
          console.log('[FileDialog] Resolving with files:', files.length);
          resolveOnce({
            canceled: files.length === 0,
            filePaths: files
          });
        };
        
        // Standard cancel event (Chrome 113+, Firefox)
        input.oncancel = () => {
          console.log('[FileDialog] oncancel fired');
          resolveOnce({ canceled: true, filePaths: [] });
        };
        
        // Add focus listener for Safari fallback
        window.addEventListener('focus', handleFocus);
        
        // Mark dialog as opened after a short delay (to avoid immediate focus trigger)
        setTimeout(() => {
          dialogOpened = true;
        }, 100);
        
        input.click();
      });
    },

    /**
     * Show message box
     * @param {Object} options 
     * @returns {Promise<{response: number}>}
     */
    async showMessageBox(options) {
      const result = window.confirm(options.message);
      return { response: result ? 0 : 1 };
    }
  },

  /**
   * Get current window - returns mock object
   */
  getCurrentWindow() {
    return {
      close() {
        // Can't close browser tab programmatically without user gesture
        console.log('Window close requested');
      },
      minimize() {
        console.log('Window minimize requested');
      },
      maximize() {
        console.log('Window maximize requested');
      }
    };
  },

  /**
   * BrowserWindow - mock for compatibility
   */
  BrowserWindow: class BrowserWindow {
    constructor(options) {
      this.options = options;
    }
    
    loadURL(url) {
      window.open(url, '_blank');
    }
    
    on(event, callback) {
      // No-op
    }
    
    show() {
      // No-op
    }
    
    close() {
      // No-op
    }
  }
};

/**
 * IPC Renderer replacement - mostly no-op
 */
export const ipcRenderer = {
  send(channel, ...args) {
    console.log(`[IPC] Send to ${channel}:`, args);
  },
  
  on(channel, callback) {
    console.log(`[IPC] Registered listener for ${channel}`);
    // No-op - no main process in browser
  },
  
  once(channel, callback) {
    console.log(`[IPC] Registered once listener for ${channel}`);
    // No-op
  },
  
  removeListener(channel, callback) {
    // No-op
  },
  
  removeAllListeners(channel) {
    // No-op
  }
};

// Default export for convenience
export default {
  shell,
  clipboard,
  remote,
  ipcRenderer
};
